import {
  Task,
  TaskQueryOrderColumn,
  TasksQuery,
} from '@/core/domain/operational/Tasks/Tasks.types';
import { ColumnsType } from 'antd/lib/table';
import { useIntl } from 'react-intl';
import { useWorkorderDetailContext } from '../../../WorkorderDetailProvider';
import formatterPrice from '@/utils/number/formatterPrice';
import { AppPageHeader, InfiniteTable } from '@/components/_common';
import { useTasks } from '@/core/domain/operational/Tasks/Tasks.hooks';
import { WorkOrderItemType } from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.types';
import { useMemo, useState, useEffect } from 'react';
import { getCompanyPrice, useWorkorderItems } from '..';
import { useDebounce, useDisclosure, useWindowSize } from '@/hooks';
import TasksExtraFilters from '@/components/Operational/Tasks/TasksExtraFilters';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { tableSorterOrder } from '@/utils/table';
import { SorterResult } from 'antd/lib/table/interface';
import { Button, Checkbox, Col, Divider, Row } from 'antd';
import { useWorkOrderItemsVerify } from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.hooks';
import { isAppPortocar } from '@/index';
import { PortoApoliceType } from '@/core/domain/porto-seguro/apolices/apolices.types';
import TaskSimpliedRegisterModal, {
  OnCallbackAddTask,
} from '@/components/_common/TaskSimpliedRegisterModal'; //OnCallbackAddTask,

const ServiceListSystem: React.FC = () => {
  const intl = useIntl();

  const {
    workorder: { data: workorder },
    complement: { data: complement },
  } = useWorkorderDetailContext();

  const { setItems, items } = useWorkorderItems();

  const { isMobile } = useWindowSize();

  const [callbackPart, setCallbackPart] = useState<OnCallbackAddTask>();
  const [taskCreateOpen, taskCreateAction] = useDisclosure();

  const [queryParams, setQueryParams] = useState<TasksQuery>({
    order: 'ASC',
    companyId: workorder?.company.id,
    active: true,
    modelId: undefined,
    isSpecificFilter: false,
  });

  const queryDebounce = useDebounce(queryParams, 500);

  const { data, isFetching, hasNextPage, fetchNextPage } =
    useTasks(queryDebounce);

  const tasks = flatInfiniteData(data);

  const { data: workorderItemsData } = useWorkOrderItemsVerify(complement?.id);
  const workorderItems = workorderItemsData?.data || [];

  const columns: ColumnsType<Task> = [
    {
      key: 'description',
      dataIndex: 'description',
      title: intl.formatMessage({ id: 'tasks.general.form.description' }),
      ellipsis: true,
      width: 600,
      sorter: true,
    },
    {
      key: 'temparioCode',
      dataIndex: 'temparioCode',
      title: intl.formatMessage({ id: 'tasks.general.form.temporaryCode' }),
      width: 120,
      sorter: true,
    },
    {
      key: 'subArea',
      dataIndex: ['subArea', 'name'],
      title: intl.formatMessage({ id: 'tasks.general.form.area' }),
      width: 120,
    },
    {
      key: 'taskPriceSet',
      dataIndex: 'taskPriceSet',
      width: 90,
      title: intl.formatMessage({ id: 'tasks.changePrice.form.price' }),
      align: 'right',
      render: (_, item) => {
        if (workorder?.company.id) {
          const task = item.taskPriceSet?.find(
            (task) => task.companyId === workorder.company.id,
          );

          if (typeof task?.price === 'number') {
            return formatterPrice(task?.price);
          }

          return '-';
        }

        return '-';
      },
    },
  ];

  function handleOrder(sorter: SorterResult<Task>) {
    if (sorter && sorter?.columnKey) {
      setQueryParams((prev) => {
        if (sorter && sorter?.columnKey) {
          const order = sorter.order
            ? tableSorterOrder[sorter.order]
            : undefined;

          const columnOrder: Record<string, TaskQueryOrderColumn> = {
            description: TaskQueryOrderColumn.Description,
            temparioCode: TaskQueryOrderColumn.Code,
          };

          if (prev.taskOrder === columnOrder[sorter.columnKey]) {
            return {
              ...prev,
              order,
            };
          }

          return {
            ...prev,
            order: tableSorterOrder.ascend,
            taskOrder: columnOrder[sorter.columnKey],
          };
        }

        return prev;
      });
    }
  }

  const minDiscountPolicy = useMemo(() => {
    if (
      isAppPortocar &&
      !workorder?.partnerDiscount &&
      workorder?.employeeDiscount
    )
      return 10;

    if (
      !isAppPortocar ||
      !complement?.policyNumber ||
      !workorder?.partnerDiscount
    )
      return undefined;

    const isPortoPolicy =
      !!complement?.policyNumber &&
      complement?.companyCode?.toUpperCase() === PortoApoliceType.PORTO;

    return isPortoPolicy ? 20 : 15;
  }, [
    isAppPortocar,
    complement?.policyNumber,
    complement?.companyCode,
    workorder?.partnerDiscount,
  ]);

  useEffect(() => {
    if (callbackPart) {
      setQueryParams((prev) => ({
        ...prev,
        search: callbackPart.task.description,
      }));
    }
  }, [callbackPart]);

  return (
    <>
      <AppPageHeader
        searchValue={queryParams?.search}
        onSearch={(value) =>
          setQueryParams((prev) => ({ ...prev, search: value }))
        }
        extraFilters={
          <TasksExtraFilters
            queryParams={queryParams}
            setQueryParams={setQueryParams}
          />
        }
      />

      <Row align="middle" gutter={[12, 12]} justify="space-between">
        <Col>
          {!!workorder?.vehicle && (
            <Checkbox
              checked={!!queryParams.modelId}
              onChange={({ target }) =>
                setQueryParams((prev) => ({
                  ...prev,
                  modelId: target.checked
                    ? workorder?.vehicle.modelId
                    : undefined,
                }))
              }
            >
              {`Mostrar somente serviços do modelo ${workorder?.vehicle.model}`}
            </Checkbox>
          )}
        </Col>

        <Col span={isMobile ? 24 : undefined}>
          <Button type="primary" block onClick={taskCreateAction.open}>
            Cadastrar serviço
          </Button>
        </Col>
      </Row>

      <Divider />

      <InfiniteTable
        loading={isFetching}
        rowKey={(row) => row.id}
        dataSource={tasks}
        columns={columns}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        onChange={(_p, _f, sorter) => {
          if (!Array.isArray(sorter)) {
            handleOrder(sorter);
          }
        }}
        rowSelection={{
          preserveSelectedRowKeys: true,
          type: 'checkbox',
          onChange: (_, services) => {
            if (!complement || !workorder) return;

            const discount = minDiscountPolicy;

            setItems(
              services.map((service) => {
                const unitPrice = getCompanyPrice(
                  service?.taskPriceSet,
                  workorder?.company.id,
                );

                const discountValue =
                  typeof discount === 'number'
                    ? ((service?.time || 0) *
                        (unitPrice || 0) *
                        (discount || 0)) /
                      100
                    : undefined;

                return {
                  complementId: complement?.id,
                  itemId: service?.id,
                  itemName: service?.description,
                  itemType: WorkOrderItemType.Service,
                  subAreaId: service?.subArea.id,
                  subArea: service?.subArea,
                  quantity:
                    !!queryParams.modelId && !!service.applicationExecutionTime
                      ? service.applicationExecutionTime
                      : service?.time,
                  discount: discount,
                  discountValue,
                  unitPrice,
                  unitCost: getCompanyPrice(
                    service?.taskPriceSet,
                    workorder?.company.id,
                    'priceFixed',
                  ),
                  isInternal: true,
                  observation: service?.note,
                };
              }),
            );
          },
          selectedRowKeys: items.map((item) => item.itemId),
          getCheckboxProps: (item) => ({
            disabled: !!workorderItems.find(
              (workorderItem) =>
                workorderItem.type === WorkOrderItemType.Service &&
                item?.id === workorderItem?.itemId,
            ),
          }),
        }}
      />

      <TaskSimpliedRegisterModal
        open={taskCreateOpen}
        onCancel={taskCreateAction.close}
        onCallback={setCallbackPart}
      />
    </>
  );
};

export default ServiceListSystem;
